import { useState } from 'react';

import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { ApiError, api, type HqAnnouncement } from '@/lib/api';
import { useApi } from '@/lib/useApi';

/** ローカル日時 (datetime-local の値) を Unix 秒に */
function toEpoch(value: string): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
}

function formatDateTime(seconds: number | null): string {
  return seconds === null ? '—' : new Date(seconds * 1000).toLocaleString('ja-JP');
}

function statusOf(announcement: HqAnnouncement, now: number) {
  if (announcement.activeFrom > now) return { label: 'SCHEDULED', variant: 'outline' as const };
  if (announcement.activeTo !== null && announcement.activeTo <= now)
    return { label: 'ENDED', variant: 'outline' as const };
  return { label: 'LIVE', variant: 'neutral' as const };
}

/** 全テナントの画面上部に出すお知らせの配信 */
export function AnnouncementsPage() {
  const { data, error, loading, reload } = useApi<{ announcements: HqAnnouncement[] }>(
    '/hq/announcements',
  );
  const [message, setMessage] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const now = Math.floor(Date.now() / 1000);

  async function create() {
    setBusy(true);
    setFormError(null);
    try {
      await api.post('/hq/announcements', {
        message: message.trim(),
        ...(toEpoch(from) !== null ? { activeFrom: toEpoch(from) } : {}),
        ...(toEpoch(to) !== null ? { activeTo: toEpoch(to) } : {}),
      });
      setMessage('');
      setFrom('');
      setTo('');
      reload();
    } catch (cause) {
      setFormError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    try {
      await api.delete(`/hq/announcements/${id}`);
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
        <h2 className="text-sm font-bold">お知らせを配信する</h2>
        <p className="text-xs text-ink-muted">
          全テナントのダッシュボード上部に表示されます。開始日時を未来にすれば配信予約になります。
        </p>
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="例: 8/20 に資産化レポートの出力項目を追加します。"
          aria-label="メッセージ"
        />
        <FieldRow>
          <Field label="開始（省略時は即時）">
            <Input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="font-mono text-xs"
            />
          </Field>
          <Field label="終了（省略時は無期限）">
            <Input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="font-mono text-xs"
            />
          </Field>
          <Button variant="primary" onClick={create} disabled={busy || message.trim() === ''}>
            配信する
          </Button>
        </FieldRow>
        {formError ? <p className="text-sm text-alert">{formError}</p> : null}
      </div>

      <StateBlock
        loading={loading}
        error={error}
        empty={data?.announcements.length === 0}
        emptyMessage="お知らせはまだありません。"
      >
        <div className="border border-line bg-surface-raised">
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>MESSAGE</Th>
                  <Th>STATUS</Th>
                  <Th>FROM</Th>
                  <Th>TO</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data?.announcements.map((announcement) => {
                  const status = statusOf(announcement, now);
                  return (
                    <tr key={announcement.id}>
                      <Td>{announcement.message}</Td>
                      <Td>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </Td>
                      <Td className="font-mono text-xs">
                        {formatDateTime(announcement.activeFrom)}
                      </Td>
                      <Td className="font-mono text-xs">{formatDateTime(announcement.activeTo)}</Td>
                      <Td className="text-right">
                        <Button disabled={busy} onClick={() => remove(announcement.id)}>
                          削除
                        </Button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        </div>
      </StateBlock>
    </div>
  );
}
